import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';

interface State {
  failed: boolean;
  reference: string | null;
}

export class ApplicationErrorBoundary extends Component<PropsWithChildren, State> {
  override state: State = { failed: false, reference: null };

  static getDerivedStateFromError(): State {
    return { failed: true, reference: createReference() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep reporting provider-neutral until production monitoring is selected.
    console.error('Uncaught PoolAppy admin error', { error, componentStack: info.componentStack });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <Box
        component="main"
        sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}
      >
        <Paper variant="outlined" sx={{ width: '100%', maxWidth: 520, p: 4 }}>
          <Stack spacing={2.5} alignItems="center">
            <ErrorOutlineIcon color="error" sx={{ fontSize: 44 }} />
            <Typography variant="h1">PoolAppy needs to reload</Typography>
            <Alert severity="error" sx={{ width: '100%' }}>
              An unexpected display error occurred. Your server data has not been changed.
            </Alert>
            <Typography color="text.secondary" textAlign="center">
              Reference: {this.state.reference}
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload application
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }
}

function createReference(): string {
  return `UI-${Date.now().toString(36).toUpperCase()}`;
}
